import { useEffect } from "react";
import { useNetworkStore } from "@/features/network-topology/store";
import { buildSimContext } from "@/features/sim-network-bridge/permissions";
import {
  configureSimContextProvider,
  DEFAULT_SIM_CONTEXT,
  getSimState,
  useSimulationStore,
} from "@/features/simulation/public";

/**
 * 工場シミュレータと工場ネットワークの双方向連携フック。
 *
 * 1. **network → sim**: tick 直前に呼ばれる供給関数を差し替え、
 *    `deriveSimPermissions(networkNodes)` を渡す。系統 offline は即時停止。
 *
 * 2. **sim → network**:
 *    - `simStateRef` の press.busy / arm.phase / transport.phase を
 *      毎フレームポーリングし、変化時のみ network ノードの `simActive` を更新。
 *    - sim の主要状態変化（`conveyorStopped` など）を network ログに追記。
 *
 * AppLayout 等のトップで 1 回だけ呼ぶ想定（多重マウント不可）。
 */
export function useSimNetworkBridge(): void {
  // network → sim
  useEffect(() => {
    configureSimContextProvider(() => buildSimContext(useNetworkStore.getState().nodes));
    return () => {
      configureSimContextProvider(() => DEFAULT_SIM_CONTEXT);
    };
  }, []);

  // sim → network: 活動フラグ + イベント
  useEffect(() => {
    let raf: number | null = null;
    let last = {
      pressBusy: false,
      armActive: false,
      transportActive: false,
      conveyorStopped: false,
      runningAtLastEvent: false,
      completedCountAtLastEvent: 0,
    };

    const loop = () => {
      const sim = getSimState();
      const simStore = useSimulationStore.getState();
      const networkStore = useNetworkStore.getState();

      const press = sim.machines.find((m) => m.kind === "press");
      const cur = {
        pressBusy: press?.busy ?? false,
        armActive: sim.arm.phase !== "idle",
        transportActive: sim.transport.phase !== "idle_at_line",
        conveyorStopped: simStore.conveyorStopped,
        runningAtLastEvent: simStore.running,
        completedCountAtLastEvent: simStore.stats.completedCount,
      };

      if (cur.pressBusy !== last.pressBusy) {
        networkStore.setSimActiveFor("press", cur.pressBusy);
      }
      if (cur.armActive !== last.armActive) {
        networkStore.setSimActiveFor("arm", cur.armActive);
      }
      if (cur.transportActive !== last.transportActive) {
        networkStore.setSimActiveFor("transport", cur.transportActive);
      }
      if (cur.conveyorStopped !== last.conveyorStopped) {
        networkStore.appendEvent({
          kind: cur.conveyorStopped ? "warning" : "info",
          nodeId: null,
          message: cur.conveyorStopped ? "Conveyor auto-stopped (buffer full)" : "Conveyor resumed",
        });
      }
      if (cur.runningAtLastEvent !== last.runningAtLastEvent) {
        networkStore.appendEvent({
          kind: "info",
          nodeId: null,
          message: cur.runningAtLastEvent ? "Simulation started" : "Simulation paused",
        });
      }
      if (cur.completedCountAtLastEvent !== last.completedCountAtLastEvent) {
        // ワーク完成イベント（高頻度すぎるので 5 個毎にだけ吐く）
        if (cur.completedCountAtLastEvent % 5 === 0 && cur.completedCountAtLastEvent !== 0) {
          networkStore.appendEvent({
            kind: "info",
            nodeId: null,
            message: `Completed ${cur.completedCountAtLastEvent} works`,
          });
        }
      }

      last = cur;
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }, []);
}
